import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  ChevronRight,
  MapPin,
  Calendar,
  DollarSign,
  FileText,
  Camera,
  Clock,
  CheckCircle2,
  Sparkles,
  Plane,
  ArrowRight,
  ExternalLink,
  Check,
  X as XIcon,
  Info,
} from "lucide-react";
import "flag-icons/css/flag-icons.min.css";

import { getNationalityConfig } from "@/lib/spainUsaNationalityConfig";
import {
  BLS_CENTERS,
  OVERVIEW_PARAGRAPHS,
  VISA_FEES_META,
  DOCUMENTS_SECTIONS,
  PHOTO_SPEC,
  PROCESSING_TIME_POINTS,
  classifyDocItem,
} from "@/lib/spainUsaGuideShared";

/**
 * Nationality-specific "Spain visa from USA" guide (SHARED TEMPLATE).
 * Design v2: modern SaaS look — deep-navy hero, trust-blue primary,
 * amber accents on Spain highlights and fees. Real SVG flags via
 * `flag-icons` (nationality flag → Spain flag route in the hero).
 *
 * Adding a new nationality still = one entry in
 * `spainUsaNationalityConfig` with `country_code`. The design travels
 * through this template unchanged.
 *
 * URL: /spain-visa-from-usa-for-{nationalitySlug}
 */
export default function SpainVisaFromUsa({ slug }) {
  const config = getNationalityConfig(slug);
  const [activeTab, setActiveTab] = useState("overview");

  if (!config) return <Navigate to="/schengen-visa-guide/spain" replace />;

  const title = `Spain Visa from USA for ${config.nationality} Citizens (2026 Guide)`;
  const metaDescription = `Step-by-step Spain Schengen tourist visa guide for ${config.nationality_plural} living in the USA. BLS application process, documents, fees, photo specs and processing time — updated for 2026.`;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: MapPin },
    { id: "fees", label: "Visa Fees", icon: DollarSign },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "photo", label: "Photo Specs", icon: Camera },
    { id: "processing", label: "Processing", icon: Clock },
  ];

  const natFlagCode = (config.country_code || "").toLowerCase();

  return (
    <div className="min-h-screen bg-white text-[#0B1B3B]" data-testid="spain-usa-guide-page">
      <SEO title={title} description={metaDescription} jsonLd={faqJsonLd} />
      <Header />

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1B3B] via-[#0B1B3B] to-[#1E3A8A] text-white">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" aria-hidden>
          <div className="absolute inset-0"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 pt-8 pb-16">
          <nav className="text-xs text-white/60 mb-6 flex items-center gap-1.5 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/schengen-visa-guide" className="hover:text-white transition-colors">Schengen Visa Guide</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white font-medium">Spain • {config.nationality_plural} in USA</span>
          </nav>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 px-3 py-1 text-xs font-semibold text-[#FDBA74] mb-5">
                <Sparkles className="w-3 h-3" /> Nationality-specific guide
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="page-h1">
                Spain Visa from USA for<br />
                <span className="text-[#F59E0B]">{config.nationality} Citizens</span>
              </h1>
              <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">
                Everything {config.nationality_plural} living in the United States need to file a Schengen tourist visa for Spain through BLS International — jurisdiction, fees, documents, photo specs and processing time.
              </p>

              {/* Meta pill badges */}
              <div className="mt-7 flex flex-wrap gap-2">
                <PillBadge icon={MapPin}    label="BLS International · US" />
                <PillBadge icon={Calendar}  label="Schengen Type C · ≤90 days / 180" />
                <PillBadge icon={Sparkles}  label="Updated Jan 2026" tone="amber" />
              </div>
            </div>

            {/* Flag route visual */}
            <FlagRoute natFlagCode={natFlagCode} natLabel={config.nationality} />
          </div>
        </div>
      </section>

      {/* ===== Nationality origin note ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 -mt-8 relative z-10">
        <div
          className="rounded-2xl bg-white shadow-xl border border-[#E2E8F0] p-6 sm:p-8 flex gap-5 items-start"
          data-testid="origin-note"
        >
          <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#F59E0B]/15 items-center justify-center flex-shrink-0">
            <span className={`fi fi-${natFlagCode} rounded-sm shadow-sm`} style={{ transform: "scale(1.6)" }} aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F59E0B] mb-2">
              For {config.nationality_plural} in the USA
            </div>
            <p className="text-[15px] text-[#0B1B3B] leading-relaxed">
              {config.origin_note}
            </p>
          </div>
        </div>
      </section>

      {/* ===== Tabs ===== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-14">
        {/* Pill tab bar */}
        <div className="mb-8 -mx-2 sm:mx-0 overflow-x-auto scrollbar-none">
          <div className="inline-flex gap-1 p-1.5 rounded-full bg-[#F1F5F9] border border-[#E2E8F0]" role="tablist">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={
                    "inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 " +
                    (active
                      ? "bg-[#2563EB] text-white shadow-md shadow-[#2563EB]/25"
                      : "text-[#475569] hover:text-[#0B1B3B] hover:bg-white")
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content card */}
        <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-sm p-6 sm:p-10">
          {activeTab === "overview" && <OverviewPane natFlagCode={natFlagCode} />}
          {activeTab === "fees" && <FeesPane config={config} />}
          {activeTab === "documents" && <DocumentsPane />}
          {activeTab === "photo" && <PhotoPane />}
          {activeTab === "processing" && <ProcessingPane />}
        </div>
      </section>

      {/* ===== FAQs ===== */}
      <section className="bg-[#F8FAFC] border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#2563EB] mb-2">
            Frequently Asked
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0B1B3B] mb-8">
            FAQs — {config.nationality} applicants in the USA
          </h2>
          <div className="space-y-3" data-testid="faqs-list">
            {config.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl bg-white border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors overflow-hidden"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 font-semibold text-[#0B1B3B]">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8] transition-transform group-open:rotate-90 flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 text-[15px] text-[#475569] leading-relaxed border-t border-[#F1F5F9] pt-4">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
          <div className="rounded-3xl bg-gradient-to-br from-[#0B1B3B] to-[#1E3A8A] p-8 sm:p-12 text-white text-center relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-64 h-64 rounded-full bg-[#F59E0B]/10 blur-3xl pointer-events-none" aria-hidden />
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Ready to prepare your Spain visa file?
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl mx-auto text-[15px] leading-relaxed">
              SchengenFile prepares your entire application file — flight PNR, hotel bookings, cover letter, travel insurance and financial documentation — ready for your BLS appointment.
            </p>
            <Link
              to="/build-your-file"
              className="mt-8 inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1B3B] font-bold px-7 py-3.5 rounded-full text-sm shadow-lg shadow-[#F59E0B]/25 transition-transform hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              data-testid="cta-build-file"
            >
              Build my visa file
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ================= Small reusable bits ================= */

function PillBadge({ icon: Icon, label, tone = "default" }) {
  const toneCls =
    tone === "amber"
      ? "bg-[#F59E0B]/15 border-[#F59E0B]/30 text-[#FDBA74]"
      : "bg-white/10 border-white/20 text-white/90 backdrop-blur-sm";
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium ${toneCls}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function FlagRoute({ natFlagCode, natLabel }) {
  // "Route" visual: applicant nationality flag → Spain flag, with a small
  // plane in between. Uses real SVG flags via `flag-icons` (not emoji).
  return (
    <div className="relative rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-6 sm:p-8">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-4 text-center">Your route</div>
      <div className="flex items-center justify-center gap-4 sm:gap-6">
        <FlagCard code={natFlagCode} label={natLabel || "You"} sublabel="Nationality" />
        <div className="flex flex-col items-center text-white/60">
          <Plane className="w-6 h-6 text-[#F59E0B]" />
          <div className="mt-1 h-px w-16 bg-gradient-to-r from-white/10 via-[#F59E0B]/60 to-white/10" />
        </div>
        <FlagCard code="es" label="Spain" sublabel="Destination" accent />
      </div>
      <div className="mt-5 text-center text-xs text-white/50">
        Applying through <span className="text-white font-medium">BLS International</span> in the United States
      </div>
    </div>
  );
}

function FlagCard({ code, label, sublabel, accent = false }) {
  // flag-icons `.fi` uses a 4:3 SVG with background-size: contain — render it
  // at its natural 4:3 ratio (96 x 72) so the SVG detail (chakra circle,
  // coat of arms) stays undistorted. NO square container, NO cover/stretch.
  return (
    <div className="flex flex-col items-center gap-2.5">
      <span
        className={`fi fi-${code}`}
        aria-label={`${label} flag`}
        style={{
          display: "block",
          width: "112px",
          height: "84px", // 4:3
          borderRadius: "6px",
          boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
          border: accent
            ? "1px solid rgba(245,158,11,0.65)"
            : "1px solid rgba(255,255,255,0.22)",
          transition: "transform 200ms",
        }}
      />
      <div className="text-center">
        <div className="text-sm font-bold text-white">{label}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">{sublabel}</div>
      </div>
    </div>
  );
}

/* ================= Panes ================= */

function OverviewPane({ natFlagCode }) {
  return (
    <div className="space-y-6" data-testid="pane-overview">
      {OVERVIEW_PARAGRAPHS.map((p, i) => (
        <p key={i} className="text-[15px] sm:text-base text-[#334155] leading-relaxed">{p}</p>
      ))}
      <div className="mt-8 pt-8 border-t border-[#F1F5F9]">
        <div className="flex items-center gap-2 mb-5">
          <span className={`fi fi-${natFlagCode} rounded-sm shadow-sm`} style={{ transform: "scale(1.1)" }} aria-hidden />
          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
          <span className="fi fi-es rounded-sm shadow-sm" style={{ transform: "scale(1.1)" }} aria-hidden />
          <h3 className="font-display font-bold text-xl text-[#0B1B3B] ml-1">BLS visa application centers in the US</h3>
        </div>
        <p className="text-sm text-[#475569] mb-5">
          Files are submitted in person at one of these eight centers — you must apply at the center that covers your state of residence.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {BLS_CENTERS.map((c) => (
            <div key={c.city} className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 hover:border-[#2563EB]/50 hover:bg-white transition-colors">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#2563EB]" />
                <div className="font-semibold text-[#0B1B3B] text-sm">{c.city}</div>
              </div>
              <div className="text-xs text-[#64748B] mt-1.5 ml-5">{c.jurisdiction}</div>
              {c.url && (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`bls-center-link-${c.city.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-2 ml-5 inline-flex items-center gap-1 text-xs text-[#2563EB]/80 hover:text-[#2563EB] hover:underline"
                >
                  Book appointment <ExternalLink className="w-3 h-3" aria-hidden="true" />
                </a>
              )}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-[#94A3B8]" data-testid="bls-disclaimer">
          SchengenFile does not sell appointments. Book directly with BLS — we prepare your document file.
        </p>
      </div>
      <div className="mt-6 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] p-5 text-sm text-[#334155] flex gap-3 items-start">
        <Sparkles className="w-4 h-4 text-[#2563EB] mt-0.5 flex-shrink-0" />
        <div>
          <strong className="text-[#0B1B3B]">When to apply.</strong> No earlier than 180 days before travel; at least 15 days before departure. Peak-season (May–September) applicants should file at the earliest window to avoid appointment shortages.
        </div>
      </div>
    </div>
  );
}

function FeesPane({ config }) {
  return (
    <div className="space-y-8" data-testid="pane-fees">
      <div>
        <h3 className="font-display font-bold text-2xl text-[#0B1B3B] mb-1.5">
          Consular visa fees
        </h3>
        <p className="text-sm text-[#64748B]">
          {config.fee_band.band_label} • Applies to {config.nationality} passport holders applying through BLS in the US.
        </p>
      </div>

      {/* Pricing-style table */}
      <div className="rounded-2xl border border-[#E2E8F0] overflow-hidden bg-white" data-testid="fees-table">
        <div className="grid grid-cols-[1fr_auto] bg-gradient-to-r from-[#F8FAFC] to-white px-5 py-3.5 border-b border-[#E2E8F0]">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">Applicant category</div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#64748B]">Consular fee</div>
        </div>
        {config.fee_band.rows.map((row, i) => (
          <div
            key={row.label}
            className={`grid grid-cols-[1fr_auto] items-center px-5 py-4 ${i > 0 ? "border-t border-[#F1F5F9]" : ""}`}
          >
            <div className="text-[#0B1B3B] font-medium">{row.label}</div>
            <div className="font-display font-bold text-lg text-[#F59E0B] tabular-nums">{row.amount}</div>
          </div>
        ))}
      </div>

      {/* Service charge card */}
      <div className="rounded-xl border border-[#F59E0B]/30 bg-[#FFFBEB] p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <DollarSign className="w-4 h-4 text-[#F59E0B]" />
          <strong className="text-[#0B1B3B]">BLS service charge: USD {VISA_FEES_META.service_charge_usd}</strong>
        </div>
        <p className="text-sm text-[#64748B] leading-relaxed">{VISA_FEES_META.service_charge_note}</p>
      </div>

      <div className="text-xs text-[#94A3B8] leading-relaxed space-y-1">
        <p>{VISA_FEES_META.currency_note}</p>
        <p className="font-semibold text-[#64748B]">{VISA_FEES_META.effective_date_note}</p>
      </div>
    </div>
  );
}

function DocIcon({ kind }) {
  // Semantic Lucide icons for the doc checklist — required/negative/info.
  // 16px, aligned to the first line of text, non-interactive.
  const common = { className: "w-4 h-4 mt-0.5 flex-shrink-0", "aria-hidden": "true" };
  if (kind === "negative") return <XIcon {...common} strokeWidth={2.25} color="#B91C1C" />;
  if (kind === "info")     return <Info  {...common} strokeWidth={2} color="#64748B" />;
  return                          <Check {...common} strokeWidth={2.25} color="#2563EB" />;
}


function DocumentsPane() {
  return (
    <div className="space-y-5" data-testid="pane-documents">
      <div>
        <h3 className="font-display font-bold text-2xl text-[#0B1B3B] mb-1.5">Documents required</h3>
        <p className="text-sm text-[#64748B]" data-testid="documents-subtitle">Everything your file must contain, section by section.</p>
      </div>
      <div className="grid gap-4">
        {DOCUMENTS_SECTIONS.map((s, si) => (
          <details
            key={s.title}
            open={si < 2}
            className="group rounded-xl border border-[#E2E8F0] bg-white overflow-hidden transition-colors hover:border-[#CBD5E1]"
          >
            <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 bg-[#F8FAFC] border-b border-[#E2E8F0] group-open:bg-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-[#2563EB]" />
                </div>
                <div className="font-display font-bold text-[#0B1B3B]">{s.title}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#94A3B8] transition-transform group-open:rotate-90 flex-shrink-0" />
            </summary>
            <ul className="p-5 space-y-3">
              {s.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-[#334155] leading-relaxed">
                  <DocIcon kind={classifyDocItem(it)} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </div>
  );
}

function PhotoPane() {
  return (
    <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10" data-testid="pane-photo">
      <div>
        <h3 className="font-display font-bold text-2xl text-[#0B1B3B] mb-6">Photo specifications</h3>
        <dl className="space-y-4">
          <SpecRow label="Size" value={PHOTO_SPEC.size_inches} />
          <SpecRow label="Background" value={PHOTO_SPEC.background} />
          <SpecRow label="Age of photo" value={PHOTO_SPEC.age} />
          <SpecRow label="Head & face" value={PHOTO_SPEC.face} />
          <SpecRow label="Expression" value={PHOTO_SPEC.expression} />
          <SpecRow label="Accessories" value={PHOTO_SPEC.accessories} />
          <SpecRow label="Print" value={PHOTO_SPEC.print} />
        </dl>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] p-6 flex flex-col items-center justify-center">
        <PhotoDiagram />
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-4 py-2 border-b border-[#F1F5F9]">
      <dt className="text-[11px] font-bold uppercase tracking-widest text-[#F59E0B] pt-1">{label}</dt>
      <dd className="text-[15px] text-[#334155] leading-relaxed">{value}</dd>
    </div>
  );
}

function PhotoDiagram() {
  // Schematic geometry — NO real person.
  return (
    <figure className="text-center" data-testid="photo-diagram">
      <svg viewBox="0 0 240 260" className="w-full max-w-[240px] mx-auto" role="img" aria-label="Schengen visa photo dimensions">
        <rect x="20" y="20" width="200" height="200" fill="white" stroke="#0B1B3B" strokeWidth="2" rx="4" />
        <rect x="60" y="55" width="120" height="140" fill="none" stroke="#F59E0B" strokeDasharray="4 3" strokeWidth="1.5" rx="4" />
        <g fill="#E2E8F0" stroke="#94A3B8" strokeWidth="1.5">
          <path d="M 80 195 Q 80 175 100 168 L 140 168 Q 160 175 160 195 L 160 200 L 80 200 Z" />
          <ellipse cx="120" cy="120" rx="34" ry="42" />
          <ellipse cx="87" cy="123" rx="5" ry="9" />
          <ellipse cx="153" cy="123" rx="5" ry="9" />
        </g>
        <g stroke="#0B1B3B" strokeWidth="1" fill="#0B1B3B">
          <line x1="20" y1="10" x2="220" y2="10" />
          <line x1="20" y1="6" x2="20" y2="14" />
          <line x1="220" y1="6" x2="220" y2="14" />
          <text x="120" y="8" textAnchor="middle" fontSize="10" fill="#0B1B3B">2 in / 51 mm</text>
        </g>
        <g stroke="#0B1B3B" strokeWidth="1">
          <line x1="10" y1="20" x2="10" y2="220" />
          <line x1="6" y1="20" x2="14" y2="20" />
          <line x1="6" y1="220" x2="14" y2="220" />
          <text x="6" y="120" fontSize="10" fill="#0B1B3B" transform="rotate(-90 6 120)" textAnchor="middle">2 in / 51 mm</text>
        </g>
        <text x="120" y="48" textAnchor="middle" fontSize="9" fill="#F59E0B" fontWeight="700">
          Face ≈ 70–80% of frame
        </text>
      </svg>
      <figcaption className="mt-4 text-xs text-[#64748B] leading-relaxed max-w-[240px] mx-auto">
        Illustrative schematic (not a real person). Face centered, ears visible, plain white background.
      </figcaption>
    </figure>
  );
}

function ProcessingPane() {
  return (
    <div className="space-y-6" data-testid="pane-processing">
      <div>
        <h3 className="font-display font-bold text-2xl text-[#0B1B3B] mb-1.5">Processing timeline</h3>
        <p className="text-sm text-[#64748B]">
          Handled by the Spanish consulate covering your BLS jurisdiction. Timelines below are typical minimums — plan generous buffer during peak season.
        </p>
      </div>
      <ol className="space-y-3">
        {PROCESSING_TIME_POINTS.map((p, i) => (
          <li key={i} className="flex items-start gap-4 rounded-xl border border-[#E2E8F0] bg-white p-4 hover:border-[#2563EB]/40 transition-colors">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white font-bold text-sm flex items-center justify-center">
              {i + 1}
            </span>
            <span className="text-[15px] text-[#334155] leading-relaxed">{p}</span>
          </li>
        ))}
      </ol>
      <div className="rounded-xl border-l-4 border-[#F59E0B] bg-[#FFFBEB] p-5 text-sm text-[#78350F] leading-relaxed">
        <strong className="text-[#92400E]">Important:</strong> even a perfectly-prepared file can be refused — Schengen visa approval is at the consulate&apos;s discretion. Never buy non-refundable travel arrangements before the visa is in your passport.
      </div>
    </div>
  );
}
